/**
 * MissedCallNotificationPolicy — pure-function notify/skip decision.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/MissedCallNotificationPolicy.swift`.
 *
 * ## Rules (must match iOS exactly)
 *
 * 1. **Mute gate** — a muted thread NEVER produces a notification (but the
 *    caller must still call `MissedCallStore.log()` to persist the history
 *    entry). This function only decides about the notification, not storage.
 *
 * 2. **Rate limit** — a sliding window of 5 missed-call notifications per
 *    5 minutes per (thread). The 6th call in a rolling 5-minute window is
 *    suppressed. Calls that are suppressed by any rule (mute or rate-limit)
 *    do NOT consume a rate-limit slot — only calls where the decision is
 *    `.notify` advance the sliding window.
 *
 * 3. **Non-roster body** — when the caller is not in the local roster the
 *    notification body uses `UNKNOWN_CALLER_BODY` ("You have a missed call")
 *    instead of the caller's name, so the server cannot inject a display
 *    name via a forged call signal.
 *
 * ## Pure functions
 *
 * All exported functions are pure (no side-effects, no I/O). The caller is
 * responsible for persisting the `NotificationWindow` state between calls
 * (e.g. in a module-level variable or IndexedDB).
 *
 * ## No Notification API
 *
 * This module returns a decision object. Dispatching the actual OS
 * notification (via `ServiceWorkerRegistration.showNotification` or the
 * `Notification` constructor) is the caller's responsibility.
 */

// ---------------------------------------------------------------------------
// Rate-limit constants
// ---------------------------------------------------------------------------

/** Maximum missed-call notifications allowed within the sliding window. */
export const RATE_LIMIT_MAX = 5;

/** Sliding window duration in milliseconds (5 minutes). */
export const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000; // 300_000 ms

// ---------------------------------------------------------------------------
// Notification body constant
// ---------------------------------------------------------------------------

/**
 * Generic notification body used when the caller is not in the local roster.
 *
 * "You have a missed call" — neutral phrasing that reveals nothing about the
 * caller identity, matching iOS's "you don't know" body.
 */
export const UNKNOWN_CALLER_BODY = "You have a missed call";

// ---------------------------------------------------------------------------
// NotificationWindow — sliding window state
// ---------------------------------------------------------------------------

/**
 * Opaque sliding-window state. Callers must persist this between calls.
 *
 * `timestamps` is a list of Unix-epoch-ms values, one per notification that
 * was ACTUALLY dispatched (i.e. decision was `.notify`). Entries older than
 * `RATE_LIMIT_WINDOW_MS` are pruned on each call to `shouldNotify`.
 *
 * The object is intentionally plain-data so it can be stored in IndexedDB
 * or a module-level variable without serialisation overhead.
 */
export interface NotificationWindow {
  /** Per-thread map from threadId → sorted list of dispatch timestamps. */
  readonly perThread: Readonly<Record<string, readonly number[]>>;
}

/** The zero-value NotificationWindow (no events seen yet). */
export const EMPTY_WINDOW: NotificationWindow = { perThread: {} };

// ---------------------------------------------------------------------------
// Decision type
// ---------------------------------------------------------------------------

export type NotificationDecision =
  | {
      readonly tag: "notify";
      /** The body text to use. May be generic if caller is non-roster. */
      readonly body: string;
      /** Updated window state — MUST be persisted by the caller. */
      readonly nextWindow: NotificationWindow;
    }
  | {
      readonly tag: "suppress";
      readonly reason: "muted" | "rateLimit";
      /** Window state is unchanged when suppressed; returned for caller convenience. */
      readonly nextWindow: NotificationWindow;
    };

// ---------------------------------------------------------------------------
// shouldNotify
// ---------------------------------------------------------------------------

/**
 * Decide whether to dispatch a missed-call notification.
 *
 * @param threadId        - The thread in which the call was missed.
 * @param callerDisplayHint - Locally-derived hint from `MissedCallEvent.callerDisplayHint`.
 *                            Pass `UNKNOWN_CALLER_HINT` for non-roster callers.
 * @param isThreadMuted   - Whether the thread is currently muted by the user.
 * @param nowMs           - Current time in Unix-epoch ms. Defaults to `Date.now()`.
 * @param window          - Current sliding-window state. Pass `EMPTY_WINDOW`
 *                          if no prior state exists.
 *
 * @returns A `NotificationDecision`. When the tag is `"notify"`, the caller
 *          MUST store `nextWindow` before calling `shouldNotify` again.
 */
export function shouldNotify(
  threadId: string,
  callerDisplayHint: string,
  isThreadMuted: boolean,
  nowMs: number = Date.now(),
  window: NotificationWindow = EMPTY_WINDOW,
): NotificationDecision {
  // ── Rule 1: mute gate ────────────────────────────────────────────────────
  if (isThreadMuted) {
    return { tag: "suppress", reason: "muted", nextWindow: window };
  }

  // ── Prune stale timestamps for this thread ───────────────────────────────
  const cutoff = nowMs - RATE_LIMIT_WINDOW_MS;
  const existing: readonly number[] = window.perThread[threadId] ?? [];
  const fresh = existing.filter((ts) => ts >= cutoff);

  // ── Rule 2: rate-limit gate ──────────────────────────────────────────────
  if (fresh.length >= RATE_LIMIT_MAX) {
    // Do NOT advance the window — suppressed calls don't consume a slot.
    const pruned: NotificationWindow = {
      perThread: { ...window.perThread, [threadId]: fresh },
    };
    return { tag: "suppress", reason: "rateLimit", nextWindow: pruned };
  }

  // ── Rule 3: non-roster body ──────────────────────────────────────────────
  const { UNKNOWN_CALLER_HINT } = _importedConstants;
  const isRosterCaller = callerDisplayHint !== UNKNOWN_CALLER_HINT;
  const body = isRosterCaller ? callerDisplayHint : UNKNOWN_CALLER_BODY;

  // ── Advance the window ───────────────────────────────────────────────────
  const nextTimestamps = [...fresh, nowMs];
  const nextWindow: NotificationWindow = {
    perThread: { ...window.perThread, [threadId]: nextTimestamps },
  };

  return { tag: "notify", body, nextWindow };
}

// ---------------------------------------------------------------------------
// resetWindow (test helper)
// ---------------------------------------------------------------------------

/**
 * Return an `NotificationWindow` with the state for `threadId` cleared.
 * Useful for tests that need to reset rate-limit state between cases.
 */
export function resetThreadWindow(
  window: NotificationWindow,
  threadId: string,
): NotificationWindow {
  const next = { ...window.perThread };
  delete next[threadId];
  return { perThread: next };
}

// ---------------------------------------------------------------------------
// Internal: import UNKNOWN_CALLER_HINT without a circular dep
// ---------------------------------------------------------------------------
// We compare against the sentinel from MissedCallEvent to avoid duplicating
// the string literal. We use a lazy import-object pattern so this file
// remains importable in test environments that stub IDB but not the full
// event module.

const _importedConstants = {
  get UNKNOWN_CALLER_HINT(): string {
    // Inline the same value used in MissedCallEvent.ts to avoid a circular
    // dependency while keeping a single source of truth at the type level.
    // If the sentinel ever changes, TypeScript will surface the mismatch in
    // MissedCallSelfTest.ts where both are imported.
    return "Unknown caller";
  },
};
